<template>
  <v-dialog
    v-model="dialog"
    width="448"
    open-on-focus
    no-click-animation
    persistent
    content-class="new-practice-dialog"
    @click:outside="onClickOutside"
    @keydown="onKeydown"
  >
    <v-card elevation="0">
      <v-form ref="form" class="new-practice-form" @submit.prevent="onSubmit">
        <div class="new-practice-form-body">
          <v-toolbar flat height="76">
            <v-toolbar-title class="new-practice-toolbar-title"
              >Add Company User</v-toolbar-title
            >
            <v-spacer></v-spacer>
            <v-btn color="accent" icon tile @click="closeDialog">
              <v-icon size="24">mdi-close</v-icon>
            </v-btn>
          </v-toolbar>
          <!-- practice -->
          <v-card-title class="new-practice-card-title pb-0"
            >User data</v-card-title
          >
          <v-container class="new-practice-card-container pt-0">
            <v-row>
              <v-col sm="6">
                <dialog-input
                  label="First name"
                  name="firstName"
                  @input="onInput"
                />
              </v-col>
              <v-col sm="6">
                <dialog-input
                  label="Last name"
                  name="lastName"
                  @input="onInput"
                />
              </v-col>
            </v-row>
          </v-container>
          <v-container class="new-practice-card-container pt-0">
            <dialog-input label="Address" name="address" @input="onInput" />
          </v-container>
          <v-container class="new-practice-card-container pt-0">
            <dialog-input
              label="Email address"
              name="email"
              prepend-icon="mdi-email-outline"
              @input="onInput"
              :validate="true"
              :validationType="'email'"
            />
          </v-container>
          <v-container class="new-practice-card-container pt-0">
            <dialog-input
              label="Phone"
              name="phone"
              prepend-icon="mdi-phone-outline"
              @input="onInput"
            />
          </v-container>
          <v-container class="new-practice-card-container py-0">
            <v-row>
              <v-col class="new-bh-user-form-col-left" sm="12">
                <dialog-select
                  label="Role"
                  name="roleId"
                  :items="roleIdOptions"
                  @input="onInput"
                />
              </v-col>
            </v-row>
          </v-container>
          <v-container
            class="new-practice-card-container py-0 pt-2"
            v-if="isAdvisorRoleSelected"
          >
            <bh-select
              class="mb-3"
              label="AI Lens"
              v-model="newUserData.aiLens"
              :items="AI_LENS_OPTIONS"
              item-text="text"
              item-value="value"
              @input="changeFeatureFlag('aiLens', $event)"
            />
          </v-container>
          <v-container class="new-practice-card-container py-0">
            <bh-select
              class="mb-3"
              :disabled="!isAnyAdminRole"
              :label="tenantTexts.newPracticeUserDialog.surveyFlag"
              v-model="newUserData.surveyAvailable"
              :items="computedFeatureFlagsSurvey"
              item-text="text"
              item-value="value"
              @input="changeFeatureFlag('surveyAvailable', $event)"
            />
          </v-container>
        </div>
        <v-container class="new-practice-card-footer d-flex justify-end">
          <v-btn
            :disabled="!formIsValid"
            rounded
            color="primary"
            type="submit"
            elevation="0"
          >
            Add user
          </v-btn>
        </v-container>
      </v-form>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex'
import DialogInput from '@/components/forms/DialogInput.vue'
import DialogSelect from '@/components/forms/DialogSelect.vue'
import emailValidation from '@/helpers/emailValidation.js'
import TenantTextsMixin from '@/mixins/tenantTexts'
import BhSelect from '../inputs/BhSelect.vue'
import { AI_LENS_OPTIONS, DEFAULT_LANGUAGE } from '@/helpers/constants'

const emptyForm = {
  firstName: '',
  lastName: '',
  address: '',
  roleId: 0,
  practiceId: 0,
  email: '',
  isActive: true,
  phone: '',
  surveyAvailable: 'Inactive',
  language: DEFAULT_LANGUAGE,
  aiLens: null,
}

export default {
  name: 'NewPracticeUserDialog',
  components: {
    DialogInput,
    DialogSelect,
    BhSelect,
  },
  props: {
    showDialog: {
      type: Boolean,
      default: false,
    },
    surveyAvailable: {
      type: String,
      default: 'Inactive',
    },
    practiceId: {
      type: Number,
      default: 0,
    },
  },
  mixins: [TenantTextsMixin],
  data() {
    return {
      AI_LENS_OPTIONS,
      dialog: false,
      newUserData: { ...emptyForm, practiceId: this.practiceId },
      typeOptions: [
        {
          text: 'Type 1',
          value: 1,
        },
        {
          text: 'Type 2',
          value: 2,
        },
      ],
      rules: {
        text: [v => !!v || 'Value cannot by empty'], // @todo
      },
    }
  },
  watch: {
    surveyAvailable: function(val) {
      if (val) {
        this.newUserData.surveyAvailable = val
      }
    },
    showDialog: function(val) {
      if (val) {
        this.dialog = true
      }
    },
  },
  computed: {
    ...mapGetters('userStates', ['getRolename']),
    ...mapState('dictionariesStates', [
      'dictionaryBhUsersRoleIds',
      'dictionaryPracticeUserRole',
      'dictionaryFeatureFlagState',
    ]),
    formIsValid() {
      const aiLensValues = AI_LENS_OPTIONS.map(el => el.value)
      const hasAiLensSelected = aiLensValues.includes(this.newUserData.aiLens)
      const isAiLensValid =
        !this.isAdvisorRoleSelected ||
        (this.isAdvisorRoleSelected && hasAiLensSelected)

      return (
        this.newUserData.firstName &&
        this.newUserData.lastName &&
        this.newUserData.address &&
        this.newUserData.phone &&
        this.newUserData.email &&
        this.validateEmail(this.newUserData.email) &&
        this.newUserData.roleId &&
        this.newUserData.surveyAvailable &&
        isAiLensValid
      )
    },
    isAnyAdminRole() {
      return (
        this.dictionaryBhUsersRoleIds[this.getRolename] ===
          this.dictionaryBhUsersRoleIds.Admin || this.isPracticeAdmin
      )
    },
    roleIdOptions() {
      return [
        {
          text: 'Administrator',
          value: this.dictionaryPracticeUserRole.Administrator,
        },
        {
          text: 'Advisor',
          value: this.dictionaryPracticeUserRole.Advisor,
        },
        {
          text: 'Team',
          value: this.dictionaryPracticeUserRole.Team,
        },
      ]
    },
    computedFeatureFlagsSurvey() {
      return [
        {
          text: 'Active',
          value: this.dictionaryFeatureFlagState[
            this.dictionaryFeatureFlagState.Active
          ],
        },
        {
          text: 'Inactive',
          value: this.dictionaryFeatureFlagState[
            this.dictionaryFeatureFlagState.Inactive
          ],
        },
      ]
    },
    isAdvisorRoleSelected() {
      return this.newUserData.roleId === this.dictionaryPracticeUserRole.Advisor
    },
  },
  methods: {
    closeDialog() {
      this.dialog = false
      this.$emit('close-dialog')
    },
    onClickOutside() {
      this.closeDialog()
    },
    onKeydown(key) {
      if (key.code === 'Escape' && key.isTrusted) this.closeDialog()
    },
    changeFeatureFlag(key, value) {
      this.onInput({ name: key, value })
    },
    onInput({ name, value }) {
      this.newUserData[name] = value
    },
    clearForm() {
      this.$refs.form.reset()
      this.newUserData = {
        ...emptyForm,
        surveyAvailable: this.surveyAvailable || emptyForm.surveyAvailable,
        practiceId: this.practiceId,
      }
    },
    onSubmit() {
      this.$emit('on-submit', {
        ...this.newUserData,
        aiLens: this.isAdvisorRoleSelected ? this.newUserData.aiLens : null,
      })
      this.clearForm()
      this.closeDialog()
    },
    validateEmail: emailValidation.validEmail,
  },
}
</script>

<style lang="scss">
@import '@/styles/variables.scss';

.new-practice {
  &-dialog {
    margin: $gutter;
    margin-left: auto;
    height: 100%;
    max-height: calc(100% - #{2 * $gutter}) !important;
    > .v-card {
      width: 100%;
      height: 100%;
      background-color: $bh-blue--4;
      overflow: auto;
    }
  }
  &-toolbar-title {
    color: $bh-blue;
    font-size: 1.5715rem;
    font-weight: bold;
  }
  &-card {
    &-title {
      font-size: $font-size-root;
      color: $bh-blue;
      padding-left: 1.5 * $gutter;
      padding-right: 1.5 * $gutter;
    }
    &-container {
      padding-left: 1.5 * $gutter;
      padding-right: 1.5 * $gutter;
    }
    &-footer {
      padding: 1.5 * $gutter;
    }
  }
  &-form {
    height: 100%;
    position: relative;
    display: flex;
    flex-direction: column;
    justify-content: space-between;

    &-body {
      ::v-deep .v-toolbar__content {
        padding-left: 1.5 * $gutter;
        padding-right: 1.5 * $gutter;
      }
    }
    &-col-left {
      padding-top: 0;
      padding-right: 0.25 * $gutter;
    }
    &-col-right {
      padding-top: 0;
      padding-left: 0.25 * $gutter;
    }
  }
}
</style>
